import { useI18n } from '@ecom/i18n';
import { Field, Input } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

/**
 * « Type your own password again » - the confirmation asked before a critical action (decision of 8 October 2026: the platform has no
 * second factor, so a stolen or unattended session must not be enough to create an account, promote a privilege, suspend or refuse a
 * shop, or reset a password).
 *
 * It is a plain password field on purpose: the browser's password manager can fill it, and nothing here stores the value - the parent
 * keeps it in state only for the duration of one request and sends it as `confirmPassword`, which the API consumes (and removes from the
 * request) before the handler runs.
 */
export function ConfirmPasswordField({
  value,
  onChange,
  error,
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error?: string;
}): ReactElement {
  const { t } = useI18n();

  return (
    <Field
      label={t('admin.common.confirmPasswordLabel')}
      hint={t('admin.common.confirmPasswordHint')}
      {...(error === undefined ? {} : { error })}
    >
      {(bind) => (
        <Input
          {...bind}
          type="password"
          // 'new-password', not 'current-password': a re-confirmation is the person typing their own password again, and the
          // browser would otherwise fill in whatever it saved for this host (every local console shares 'localhost', so
          // it could be another account's password, and the server refused the action).
          autoComplete="new-password"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
    </Field>
  );
}
